import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { listCoachSessions, AssessmentId } from "../../../services/rest/kairaAssessments";

export type CareTriadKey = "diet" | "exercise" | "success";

export const VIEW_TO_ASSESSMENT: Record<CareTriadKey, AssessmentId> = {
  diet: "diet",
  exercise: "exercise",
  success: "mind",
};

export const ASSESSMENT_VIEWS: CareTriadKey[] = ["diet", "exercise", "success"];

export const enum AssessmentMode {
  Baseline = "baseline",
  Followup = "followup",
}
// Keep the union type alias for external callers that use it as a type
export type AssessmentModeValue = `${AssessmentMode}`;

type ProgressMap = Record<CareTriadKey, number>;
type LabelMap = Record<CareTriadKey, string>;
type BaselineMap = Record<CareTriadKey, boolean>;

export type AssessmentChromeSession = {
  sessionId: string;
  label: string;
  date: string;
  action: string;
  source: string;
  canCancel: boolean;
};

export type AssessmentChrome = {
  sessions: AssessmentChromeSession[];
  activeSessionId: string | null;
  occurrence: string | null;
  activeDate: string;
  action: string;
  source: string;
  canFollowUp: boolean;
  saving: boolean;
  mode?: AssessmentMode;
  selectSession: (sessionId: string) => void;
  newFollowUp: () => void;
  cancelFollowUp: (sessionId?: string) => void;
};

type Ctx = {
  pctByView: ProgressMap;
  labelByView: LabelMap;
  baselinePublishedByView: BaselineMap;
  setPct: (view: CareTriadKey, pct: number) => void;
  refreshAll: () => Promise<void>;
  chrome: AssessmentChrome | null;
  registerChrome: (chrome: AssessmentChrome | null) => void;
};

const AssessmentProgressContext = createContext<Ctx | null>(null);

const EMPTY: ProgressMap = { diet: 0, exercise: 0, success: 0 };
const EMPTY_LABELS: LabelMap = { diet: "", exercise: "", success: "" };

const visitLabel = (session: any) => {
  if (session?.occurrence) return String(session.occurrence);
  const count = session?.assessmentCount;
  if (count === 0) return "Baseline";
  if (typeof count === "number") return `Follow-up ${count}`;
  return "";
};
const EMPTY_BASELINE: BaselineMap = {
  diet: false,
  exercise: false,
  success: false,
};

/** Completion for overview/sidebar bars — 0–100 only. */
export const clampPct = (value: unknown) => {
  const numericPct = Number(value);
  if (!Number.isFinite(numericPct) || numericPct <= 0) return 0;
  return Math.min(100, Math.round(numericPct));
};

/** Baseline = assessmentCount 0 and published. */
const isBaselinePublished = (sessions: any[]) =>
  sessions.some((row: any) => {
    const count =
      typeof row?.assessmentCount === "number" ? row.assessmentCount : null;
    return count === 0 && (row?.action || "") === "published";
  });

export const AssessmentProgressProvider: React.FC<{
  patientId?: string | null;
  children: React.ReactNode;
}> = ({ patientId, children }) => {
  const [pctByView, setPctByView] = useState<ProgressMap>(EMPTY);
  const [labelByView, setLabelByView] = useState<LabelMap>(EMPTY_LABELS);
  const [baselinePublishedByView, setBaselinePublishedByView] =
    useState<BaselineMap>(EMPTY_BASELINE);
  const [chrome, setChrome] = useState<AssessmentChrome | null>(null);
  const refreshGenRef = useRef(0);

  const setPct = useCallback((view: CareTriadKey, pct: number) => {
    const nextPct = clampPct(pct);
    setPctByView((prev) =>
      prev[view] === nextPct ? prev : { ...prev, [view]: nextPct },
    );
  }, []);

  const registerChrome = useCallback((next: AssessmentChrome | null) => {
    setChrome(next);
  }, []);

  const refreshAll = useCallback(async () => {
    if (!patientId) {
      setPctByView(EMPTY);
      setLabelByView(EMPTY_LABELS);
      setBaselinePublishedByView(EMPTY_BASELINE);
      return;
    }
    const gen = ++refreshGenRef.current;
    const next = { ...EMPTY };
    const nextLabels = { ...EMPTY_LABELS };
    const nextBaseline = { ...EMPTY_BASELINE };
    await Promise.all(
      ASSESSMENT_VIEWS.map(async (view) => {
        try {
          const data = await listCoachSessions(
            patientId,
            VIEW_TO_ASSESSMENT[view],
          );
          const sessions = data?.sessions || [];
          const latest = [...sessions].sort((a: any, b: any) => {
            const ca =
              typeof a?.assessmentCount === "number" ? a.assessmentCount : -1;
            const cb =
              typeof b?.assessmentCount === "number" ? b.assessmentCount : -1;
            if (cb !== ca) return cb - ca;
            return (
              new Date(b.updatedAt || b.date || 0).getTime() -
              new Date(a.updatedAt || a.date || 0).getTime()
            );
          })[0];
          next[view] = clampPct(latest?.pct);
          nextLabels[view] = visitLabel(latest);
          nextBaseline[view] = isBaselinePublished(sessions);
        } catch {
          next[view] = 0;
          nextLabels[view] = "";
          nextBaseline[view] = false;
        }
      }),
    );
    if (gen !== refreshGenRef.current) return;
    setPctByView(next);
    setLabelByView(nextLabels);
    setBaselinePublishedByView(nextBaseline);
  }, [patientId]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!patientId) setChrome(null);
  }, [patientId]);

  const value = useMemo(
    () => ({
      pctByView,
      labelByView,
      baselinePublishedByView,
      setPct,
      refreshAll,
      chrome,
      registerChrome,
    }),
    [pctByView, labelByView, baselinePublishedByView, setPct, refreshAll, chrome, registerChrome],
  );

  return (
    <AssessmentProgressContext.Provider value={value}>
      {children}
    </AssessmentProgressContext.Provider>
  );
};

export const useAssessmentProgress = () => {
  const ctx = useContext(AssessmentProgressContext);
  if (!ctx) {
    return {
      pctByView: EMPTY,
      labelByView: EMPTY_LABELS,
      baselinePublishedByView: EMPTY_BASELINE,
      setPct: () => undefined,
      refreshAll: async () => undefined,
      chrome: null,
      registerChrome: () => undefined,
    } as Ctx;
  }
  return ctx;
};
